import type { ScheduleTask } from '../../../services/scheduleService';

export function escapeHtml(s: string): string {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

/** Strip leading dependency icon from gantt row text for print labels. */
export function stripScheduleIconPrefix(text: string): string {
  return String(text || '')
    .replace(/^\s*⛓\s+/, '')
    .replace(/\s*\[CONFLICT\]\s*$/i, '')
    .trim();
}

export function formatDependencyNotes(
  deps: ScheduleTask['dependencies'] | null | undefined,
  sourceTaskIdToLabel: Map<number, string>
): string {
  if (!deps) return '';
  const parts: string[] = [];
  if (Array.isArray(deps.triggers) && deps.triggers.length > 0) {
    parts.push(`Triggers: ${deps.triggers.join(', ')}`);
  }
  if (deps.resourceDependentType) {
    parts.push(`Resource dependent: ${deps.resourceDependentType}`);
  }
  if (deps.otherComment && String(deps.otherComment).trim()) {
    parts.push(String(deps.otherComment).trim());
  }
  if (deps.completionTaskId != null) {
    const id = Number(deps.completionTaskId);
    const label = sourceTaskIdToLabel.get(id) ?? `Task #${id}`;
    parts.push(`Completion of: ${label}`);
  }
  return parts.join(' | ');
}

/**
 * Serialized into print window; draws FS-style arrows from predecessor bar end to successor bar start.
 */
export function buildPrintDependencyArrowInitScript(links: Array<{ source: number; target: number }>): string {
  const json = JSON.stringify(links).replace(/</g, '\\u003c');
  return `
(function(){
  var LINKS = ${json};
  function draw() {
    var svg = document.getElementById('schedule-print-arrows');
    if (!svg) return;
    while (svg.firstChild) { svg.removeChild(svg.firstChild); }

    var defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    var marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
    marker.setAttribute('id', 'schedule-print-arrowhead');
    marker.setAttribute('markerWidth', '7');
    marker.setAttribute('markerHeight', '7');
    marker.setAttribute('refX', '6');
    marker.setAttribute('refY', '3.5');
    marker.setAttribute('orient', 'auto');
    var poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    poly.setAttribute('points', '0 0, 7 3.5, 0 7');
    poly.setAttribute('fill', '#1a73e8');
    marker.appendChild(poly);
    defs.appendChild(marker);
    svg.appendChild(defs);

    var w = Math.max(document.documentElement.scrollWidth || 0, document.body.scrollWidth || 0);
    var h = Math.max(document.documentElement.scrollHeight || 0, document.body.scrollHeight || 0);
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));

    LINKS.forEach(function(link) {
      var fromHost = document.querySelector('[data-print-node-id="' + link.source + '"]');
      var toHost = document.querySelector('[data-print-node-id="' + link.target + '"]');
      if (!fromHost || !toHost) return;
      var fromBar = fromHost.querySelector('.print-bar-end');
      var toBar = toHost.querySelector('.print-bar-start');
      if (!fromBar || !toBar) return;
      var r1 = fromBar.getBoundingClientRect();
      var r2 = toBar.getBoundingClientRect();
      var x1 = r1.right + (window.scrollX || 0);
      var y1 = r1.top + r1.height / 2 + (window.scrollY || 0);
      var x2 = r2.left + (window.scrollX || 0);
      var y2 = r2.top + r2.height / 2 + (window.scrollY || 0);
      var dx = x2 - x1;
      var midX = x1 + Math.max(12, dx * 0.4);
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      var d = 'M ' + x1 + ' ' + y1 + ' C ' + midX + ' ' + y1 + ', ' + midX + ' ' + y2 + ', ' + x2 + ' ' + y2;
      path.setAttribute('d', d);
      path.setAttribute('stroke', '#1a73e8');
      path.setAttribute('stroke-width', '1.25');
      path.setAttribute('fill', 'none');
      path.setAttribute('marker-end', 'url(#schedule-print-arrowhead)');
      svg.appendChild(path);
    });
  }
  function run() {
    setTimeout(function() {
      draw();
      window.print();
    }, 150);
  }
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run);
})();
`;
}
